import { useMemo } from 'react';
import {
  Input,
  Checkbox,
  VStack,
  Select,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';

import getDateFromString from 'helpers/getDateFromString';
import SelectSearch from 'components/fields/SelectSearch';
import MultiSelect from 'components/fields/MultiSelect';
import {
  SectionCard,
  ValidatedField,
  ValidatedDateField,
} from '../../components';
import { useAnswerCodes, ValidationEntityProvider } from '../../hooks';
import type { Location } from 'api';

interface DateDetailsCardProps {
  location: Location;
  locationIndex: number;
  onUpdate: (index: number, field: keyof Location, value: unknown) => void;
  isUpdating?: boolean;
  height?: string;
}

const DateDetailsCard = ({
  location,
  locationIndex,
  onUpdate,
  isUpdating,
  height,
}: DateDetailsCardProps) => {
  const { getCodeByLabel, getOptions, shouldShowConditionalField, isLoading } =
    useAnswerCodes();

  const isFieldLocked = (field: string) =>
    location.itv_locked_fields?.includes(field) ?? false;

  const smokersNoCode = useMemo(
    () => getCodeByLabel('No', 'resident_smokers'),
    [getCodeByLabel],
  );

  const firstTimeBuyerYesCode = useMemo(
    () => getCodeByLabel('Yes', 'first_time_homebuyer'),
    [getCodeByLabel],
  );

  const isSmokeFree = location.resident_smokers === smokersNoCode;
  const isFirstTimeBuyer =
    location.first_time_homebuyer === firstTimeBuyerYesCode;

  const showEldestOccupantBirthdate = useMemo(
    () =>
      shouldShowConditionalField(
        'eldest_occupant_birthdate',
        location.location_type,
      ),
    [shouldShowConditionalField, location.location_type],
  );

  const showLocationAccessibility = useMemo(
    () =>
      shouldShowConditionalField(
        'location_accessibility',
        location.location_type,
      ),
    [shouldShowConditionalField, location.location_type],
  );

  const eldestOccupantBirthdate = useMemo(
    () => getDateFromString(location.eldest_occupant_birthdate ?? ''),
    [location.eldest_occupant_birthdate],
  );

  const occupancyDate = useMemo(
    () => getDateFromString(location.occupancy_date ?? ''),
    [location.occupancy_date],
  );

  const handleYearBuiltChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value === '' ? null : parseInt(e.target.value, 10);
    onUpdate(
      locationIndex,
      'year_built_yyyy',
      isNaN(value as number) ? null : value,
    );
  };

  const handleOccupancyDateChange = (date: Date | null) => {
    if (!date) {
      onUpdate(locationIndex, 'occupancy_date', null);
      return;
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    onUpdate(locationIndex, 'occupancy_date', `${year}-${month}-${day}`);
  };

  const handleEldestOccupantBirthdateChange = (date: Date | null) => {
    if (!date) {
      onUpdate(locationIndex, 'eldest_occupant_birthdate', null);
      return;
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    onUpdate(
      locationIndex,
      'eldest_occupant_birthdate',
      `${year}-${month}-${day}`,
    );
  };

  const handleOccupancyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onUpdate(locationIndex, 'occupancy', e.target.value);
  };

  const handleSmokeFreeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      onUpdate(locationIndex, 'resident_smokers', smokersNoCode);
    } else {
      onUpdate(locationIndex, 'resident_smokers', '');
    }
  };

  const handleInLawSuiteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onUpdate(locationIndex, 'in_law_suite', e.target.checked);
  };

  const handleFirstTimeBuyerChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (e.target.checked) {
      onUpdate(locationIndex, 'first_time_homebuyer', firstTimeBuyerYesCode);
    } else {
      onUpdate(locationIndex, 'first_time_homebuyer', '');
    }
  };

  const handleDwellingUseChange = (value: string | number | null) => {
    onUpdate(locationIndex, 'dwelling_use', value ?? '');
  };

  const handleLocationAccessibilityChange = (value: string | number | null) => {
    onUpdate(locationIndex, 'location_accessibility', value ?? '');
  };

  const handleOtherLocationAccessibilityChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    onUpdate(locationIndex, 'other_location_accessibility', e.target.value);
  };

  const handleNumberOfDogsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value === '' ? null : parseInt(e.target.value, 10);
    const numDogs = isNaN(value as number) ? null : value;
    onUpdate(locationIndex, 'number_of_dogs_in_the_household', numDogs);
    if (!numDogs || numDogs === 0) {
      onUpdate(locationIndex, 'breeds_of_dogs', []);
    }
  };

  const handleBreedsOfDogsChange = (ids: number[]) => {
    const codes = ids.map((id) => id.toString());
    onUpdate(locationIndex, 'breeds_of_dogs', codes);
  };

  const dogBreedItems = useMemo(
    () =>
      getOptions('breeds_of_dogs')
        .filter((opt) => opt.value !== '')
        .map((opt) => ({
          id: parseInt(String(opt.value), 10),
          name: opt.label,
        })),
    [getOptions],
  );

  const selectedDogBreedIds = useMemo(
    () => (location.breeds_of_dogs || []).map((code) => parseInt(code, 10)),
    [location.breeds_of_dogs],
  );

  const showBreedsOfDogs = (location.number_of_dogs_in_the_household ?? 0) > 0;

  return (
    <ValidationEntityProvider
      entity={location as unknown as Record<string, unknown>}
    >
      <SectionCard title="Date/Details" height={height}>
        <VStack spacing={4} align="stretch">
          <ValidatedField
            itemPath="location"
            fieldPath="year_built_yyyy"
            label="Year Built"
            isDisabled={isUpdating || isFieldLocked('year_built_yyyy')}
          >
            <Input
              variant="main"
              size="sm"
              type="number"
              min={1800}
              max={2099}
              value={location.year_built_yyyy ?? ''}
              onChange={handleYearBuiltChange}
              isDisabled={isUpdating || isFieldLocked('year_built_yyyy')}
              placeholder="YYYY"
            />
          </ValidatedField>

          {shouldShowConditionalField(
            'occupancy_date',
            location.location_type,
          ) && (
            <ValidatedDateField
              itemPath="location"
              fieldPath="occupancy_date"
              label="Occupied Since"
              date={occupancyDate}
              onChange={handleOccupancyDateChange}
              isDisabled={isUpdating}
            />
          )}

          {showEldestOccupantBirthdate && (
            <ValidatedDateField
              itemPath="location"
              fieldPath="eldest_occupant_birthdate"
              label="Eldest Occupant DOB"
              date={eldestOccupantBirthdate}
              onChange={handleEldestOccupantBirthdateChange}
              isDisabled={isUpdating}
            />
          )}

          <ValidatedField
            itemPath="location"
            fieldPath="occupancy"
            label="Occupancy"
            isDisabled={isUpdating}
          >
            <Select
              variant="main"
              size="sm"
              value={location.occupancy || ''}
              onChange={handleOccupancyChange}
              isDisabled={isUpdating}
            >
              {getOptions('occupancy').map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </ValidatedField>

          {shouldShowConditionalField(
            'occupants_relation_to_insured',
            location.occupancy,
          ) && (
            <ValidatedField
              itemPath="location"
              fieldPath="occupants_relation_to_insured"
              label="Occupant's Relation to Insured"
              isDisabled={isUpdating}
            >
              <Select
                variant="main"
                size="sm"
                value={location.occupants_relation_to_insured || ''}
                onChange={(e) =>
                  onUpdate(
                    locationIndex,
                    'occupants_relation_to_insured',
                    e.target.value,
                  )
                }
                isDisabled={isUpdating}
              >
                {getOptions('occupants_relation_to_insured').map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </ValidatedField>
          )}

          <ValidatedField
            itemPath="location"
            fieldPath="dwelling_use"
            label="Dwelling Use"
            isDisabled={isUpdating}
          >
            <SelectSearch
              options={getOptions('dwelling_use')}
              value={location.dwelling_use || ''}
              onChange={handleDwellingUseChange}
              placeholder="Select dwelling use..."
              isDisabled={isUpdating}
              isLoading={isLoading}
            />
          </ValidatedField>

          {showLocationAccessibility && (
            <ValidatedField
              itemPath="location"
              fieldPath="location_accessibility"
              label="Location Accessibility"
              isDisabled={isUpdating}
            >
              <SelectSearch
                options={getOptions('location_accessibility')}
                value={location.location_accessibility || ''}
                onChange={handleLocationAccessibilityChange}
                placeholder="Select accessibility..."
                isDisabled={isUpdating}
                isLoading={isLoading}
              />
            </ValidatedField>
          )}

          {showLocationAccessibility &&
            shouldShowConditionalField(
              'other_location_accessibility',
              location.location_accessibility,
            ) && (
              <FormControl>
                <FormLabel fontSize="sm">Specify Accessibility</FormLabel>
                <Input
                  variant="main"
                  size="sm"
                  value={location.other_location_accessibility || ''}
                  onChange={handleOtherLocationAccessibilityChange}
                  placeholder="Enter accessibility details..."
                  isDisabled={isUpdating}
                />
              </FormControl>
            )}

          <FormControl>
            <FormLabel fontSize="sm"># of Dogs</FormLabel>
            <Input
              variant="main"
              size="sm"
              type="number"
              min={0}
              max={99}
              value={location.number_of_dogs_in_the_household ?? ''}
              onChange={handleNumberOfDogsChange}
              isDisabled={isUpdating}
              placeholder="0"
            />
          </FormControl>

          {showBreedsOfDogs && dogBreedItems.length > 0 && (
            <FormControl>
              <FormLabel fontSize="sm">Dog Breeds</FormLabel>
              <MultiSelect
                selectedIds={selectedDogBreedIds}
                availableItems={dogBreedItems}
                isLoading={isLoading}
                onChange={handleBreedsOfDogsChange}
                label="Select dog breeds"
                itemLabelSingular="breed"
                itemLabelPlural="breeds"
                isDisabled={isUpdating}
                showTags={false}
              />
            </FormControl>
          )}

          <VStack spacing={3} align="stretch">
            <Checkbox
              isChecked={location.in_law_suite}
              onChange={handleInLawSuiteChange}
              isDisabled={isUpdating}
            >
              In-Law Suite
            </Checkbox>

            <Checkbox
              isChecked={isSmokeFree}
              onChange={handleSmokeFreeChange}
              isDisabled={isUpdating}
            >
              Smoke-Free Household
            </Checkbox>

            <Checkbox
              isChecked={isFirstTimeBuyer}
              onChange={handleFirstTimeBuyerChange}
              isDisabled={isUpdating}
            >
              First-Time Buyer
            </Checkbox>
          </VStack>
        </VStack>
      </SectionCard>
    </ValidationEntityProvider>
  );
};

export default DateDetailsCard;
